import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { SourceIconBadge } from '../../SourceLogo/SourceLogo';
import './OptionListPopover.css';

const SEARCHABLE_THRESHOLD = 8;

/**
 * Single-select option list used inside a FilterPill popover.
 * Selecting an option commits immediately — no Apply step.
 * A search box appears automatically once the list is long enough.
 */
const OptionListPopover = ({
    options,
    value,
    onSelect,
    searchPlaceholder = 'Search',
    searchable = options.length > SEARCHABLE_THRESHOLD,
}) => {
    const [query, setQuery] = useState('');
    const searchRef = useRef(null);

    useEffect(() => {
        if (searchable) searchRef.current?.focus();
    }, [searchable]);

    const filteredOptions = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return options;
        return options.filter((option) => option.label.toLowerCase().includes(needle));
    }, [options, query]);

    // When any option carries an icon, every row reserves the icon slot so
    // labels stay aligned (e.g. "All sources" has no icon).
    const hasIcons = useMemo(() => options.some((option) => option.iconKey), [options]);

    return (
        <div className="option-list">
            {searchable && (
                <div className="option-list__search">
                    <Search size={14} className="option-list__search-icon" aria-hidden="true" />
                    <input
                        ref={searchRef}
                        type="text"
                        className="option-list__search-input"
                        placeholder={searchPlaceholder}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        aria-label={searchPlaceholder}
                    />
                    {query && (
                        <button
                            type="button"
                            className="option-list__search-clear"
                            onClick={() => setQuery('')}
                            aria-label="Clear search"
                        >
                            <X size={13} />
                        </button>
                    )}
                </div>
            )}

            <ul className="option-list__items" role="listbox">
                {filteredOptions.length === 0 ? (
                    <li className="option-list__empty">No matches for “{query.trim()}”</li>
                ) : (
                    filteredOptions.map((option) => {
                        const selected = option.value === value;
                        return (
                            <li key={option.value} role="presentation">
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={selected}
                                    className={`option-list__item${selected ? ' is-selected' : ''}`}
                                    onClick={() => onSelect(option.value)}
                                >
                                    {hasIcons && (
                                        option.iconKey ? (
                                            <SourceIconBadge
                                                iconKey={option.iconKey}
                                                size="sm"
                                                className="option-list__item-icon"
                                            />
                                        ) : (
                                            <span className="option-list__item-icon option-list__item-icon--empty" aria-hidden="true" />
                                        )
                                    )}
                                    <span className="option-list__item-text">
                                        <span className="option-list__item-label">{option.label}</span>
                                        {option.description && (
                                            <span className="option-list__item-description">{option.description}</span>
                                        )}
                                    </span>
                                    {selected && <Check size={14} className="option-list__check" aria-hidden="true" />}
                                </button>
                            </li>
                        );
                    })
                )}
            </ul>
        </div>
    );
};

export default OptionListPopover;
